import React, { useState, useRef } from 'react';
import { Phone, MessageCircle, Upload, X, CheckCircle, AlertCircle, Camera } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import TextLink from './TextLink';
import { enrichFormData } from '../utils/attribution';

const MOLD_ISSUES = [
  "Visible mold on walls or ceilings",
  "Musty odor with no visible source",
  "Mold after a plumbing leak",
  "Attic or roof-leak mold",
  "Mold behind drywall",
  "Mold in a commercial property",
  "Water damage that hasn't dried properly",
  "Need repairs after mold removal"
];

const PROPERTY_TYPES = [
  { value: "residential", label: "Residential" },
  { value: "commercial", label: "Commercial" },
  { value: "multifamily", label: "Multi-Family" },
  { value: "other", label: "Other" }
];

const MoldLeadForm = ({ className = "", variant = "full" }) => {
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    zipCode: '',
    propertyType: 'residential',
    issueType: '',
    moistureSource: '',
    firstNoticed: '',
    hasAssessment: 'not-sure',
    details: '',
    preferredContact: 'call',
    consent: false
  });

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleIssueSelect = (issue) => {
    setFormData(prev => ({ ...prev, issueType: issue }));
  };

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter(file => {
      const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];
      const maxSize = 10 * 1024 * 1024; // 10MB
      return validTypes.includes(file.type) && file.size <= maxSize;
    });

    if (validFiles.length < files.length) {
      toast.error('Some files were skipped. Please use JPG, PNG, or WebP under 10MB.');
    }

    if (uploadedFiles.length + validFiles.length > 5) {
      toast.error('Maximum 5 photos allowed.');
      return;
    }

    const newFiles = validFiles.map(file => ({
      file,
      preview: URL.createObjectURL(file),
      name: file.name
    }));

    setUploadedFiles(prev => [...prev, ...newFiles]);
  };

  const removeFile = (index) => {
    setUploadedFiles(prev => {
      const newFiles = [...prev];
      URL.revokeObjectURL(newFiles[index].preview);
      newFiles.splice(index, 1);
      return newFiles;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.consent) {
      toast.error('Please agree to be contacted.');
      return;
    }

    setIsSubmitting(true);

    try {
      // Split name into first and last (handle single name case)
      const nameParts = formData.name.trim().split(' ');
      const firstName = nameParts[0] || formData.name || 'Customer';
      const lastName = nameParts.length > 1 ? nameParts.slice(1).join(' ') : '';
      
      // Enrich with attribution tracking
      const enrichedData = enrichFormData({
        firstName,
        lastName,
        phone: formData.phone,
        email: formData.email || null,  // Send null if empty, not empty string
        service: 'mold-remediation',
        city: formData.zipCode,
        message: `[Mold Project Request] Property: ${formData.propertyType} | Issue: ${formData.issueType || 'Not specified'} | Moisture Source: ${formData.moistureSource || 'Unknown'} | First Noticed: ${formData.firstNoticed || 'Unknown'} | Has Assessment: ${formData.hasAssessment} | Preferred Contact: ${formData.preferredContact} | Photos: ${uploadedFiles.length} | Details: ${formData.details || 'None'}`,
        pageUri: window.location.href
      }, 'mold_lead_form');

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/leads/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(enrichedData)
      });

      if (response.ok) {
        setIsSuccess(true);
        // Fire tracking event (no PII)
        if (window.dataLayer) {
          window.dataLayer.push({
            event: 'mold_lead_complete',
            page_type: 'mold_service',
            service_name: 'mold-remediation',
            form_step: 'complete'
          });
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Form submission failed:', errorData);
        throw new Error('Submission failed');
      }
    } catch (error) {
      console.error('Form error:', error);
      toast.error('Something went wrong. Please call us directly at 214-908-7644.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <div className={`bg-white rounded-2xl p-8 shadow-lg border border-neutral-200 ${className}`}>
        <div className="text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h3 className="text-2xl font-bold text-neutral-900 mb-2">Request Received</h3>
          <p className="text-neutral-600 mb-6">
            Thank you for contacting Gray Contracting. Our team will review your mold project details and reach out soon.
          </p>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-amber-800">
              <strong>Need immediate assistance?</strong><br />
              Call us directly at <a href="tel:+12149087644" className="font-semibold underline">214-908-7644</a>
            </p>
          </div>
          <div className="text-left bg-neutral-50 rounded-lg p-4">
            <p className="text-sm font-medium text-neutral-700 mb-2">While you wait, it helps to gather:</p>
            <ul className="text-sm text-neutral-600 space-y-1">
              <li>• Photos of the affected area (if not already sent)</li>
              <li>• Information about the moisture source</li>
              <li>• Any previous inspection or assessment reports</li>
              <li>• Insurance policy information (if applicable)</li>
            </ul>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-2xl shadow-lg border border-neutral-200 overflow-hidden ${className}`}>
      {/* Header */}
      <div className="bg-gradient-to-r from-neutral-900 to-neutral-800 px-6 py-5">
        <h3 className="text-xl font-bold text-white">Request a Mold Project Consultation</h3>
        <p className="text-neutral-300 text-sm mt-1">Tell us about your situation and we'll help you understand the next step.</p>
      </div>

      <form onSubmit={handleSubmit} className="p-6">
        {step === 1 && (
          <div className="space-y-5">
            {/* Issue Selector */}
            <div>
              <Label className="text-neutral-700 font-medium mb-3 block">What are you seeing in your property?</Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {MOLD_ISSUES.map((issue) => (
                  <button
                    key={issue}
                    type="button"
                    onClick={() => handleIssueSelect(issue)}
                    className={`p-3 text-left text-sm rounded-lg border-2 transition-all ${
                      formData.issueType === issue
                        ? 'border-amber-500 bg-amber-50 text-amber-800'
                        : 'border-neutral-200 hover:border-amber-300 text-neutral-700'
                    }`}
                  >
                    {issue}
                  </button>
                ))}
              </div>
            </div>

            {/* Basic Info */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="mold-name" className="text-neutral-700">Your Name *</Label>
                <Input
                  id="mold-name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="First and last name"
                  required
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="mold-phone" className="text-neutral-700">Phone Number *</Label>
                <Input
                  id="mold-phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="(214) 555-1234"
                  required
                  className="mt-1"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="mold-zip" className="text-neutral-700">Property ZIP Code *</Label>
                <Input
                  id="mold-zip"
                  name="zipCode"
                  value={formData.zipCode}
                  onChange={handleInputChange}
                  placeholder="75001"
                  required
                  maxLength={5}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="mold-property" className="text-neutral-700">Property Type</Label>
                <select
                  id="mold-property"
                  name="propertyType"
                  value={formData.propertyType}
                  onChange={handleInputChange}
                  className="mt-1 w-full h-10 px-3 rounded-md border border-neutral-200 bg-white text-sm"
                >
                  {PROPERTY_TYPES.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <Button
              type="button"
              onClick={() => setStep(2)}
              disabled={!formData.name || !formData.phone || !formData.zipCode}
              className="w-full bg-amber-500 hover:bg-amber-600 text-white font-semibold py-5"
            >
              Continue
            </Button>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-5">
            {/* Photo Upload */}
            <div>
              <Label className="text-neutral-700 font-medium mb-2 block">Upload Photos (Optional)</Label>
              <p className="text-sm text-neutral-500 mb-3">Photos help us understand your situation better. Max 5 photos, 10MB each.</p>
              
              <div 
                onClick={() => fileInputRef.current?.click()}
                className="border-2 border-dashed border-neutral-300 rounded-lg p-6 text-center cursor-pointer hover:border-amber-400 transition-colors"
              >
                <Camera className="w-8 h-8 text-neutral-400 mx-auto mb-2" />
                <p className="text-sm text-neutral-600">Click to upload photos</p>
                <p className="text-xs text-neutral-400 mt-1">JPG, PNG, WebP up to 10MB</p>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
                multiple
                onChange={handleFileUpload}
                className="hidden"
              />

              {uploadedFiles.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3">
                  {uploadedFiles.map((file, index) => (
                    <div key={index} className="relative">
                      <img
                        src={file.preview}
                        alt={`Upload ${index + 1}`}
                        className="w-16 h-16 object-cover rounded-lg"
                      />
                      <button
                        type="button"
                        onClick={() => removeFile(index)}
                        className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Additional Details */}
            <div>
              <Label htmlFor="mold-moisture" className="text-neutral-700">Known source of moisture? (Optional)</Label>
              <Input
                id="mold-moisture"
                name="moistureSource"
                value={formData.moistureSource}
                onChange={handleInputChange}
                placeholder="e.g., Plumbing leak, roof leak, AC condensation"
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="mold-noticed" className="text-neutral-700">When did you first notice it? (Optional)</Label>
              <Input
                id="mold-noticed"
                name="firstNoticed"
                value={formData.firstNoticed}
                onChange={handleInputChange}
                placeholder="e.g., Last week, after the storm"
                className="mt-1"
              />
            </div>

            <div>
              <Label className="text-neutral-700">Has a mold assessment been completed?</Label>
              <div className="flex gap-4 mt-2">
                {[
                  { value: 'yes', label: 'Yes' },
                  { value: 'no', label: 'No' },
                  { value: 'not-sure', label: 'Not Sure' }
                ].map(option => (
                  <label key={option.value} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="hasAssessment"
                      value={option.value}
                      checked={formData.hasAssessment === option.value}
                      onChange={handleInputChange}
                      className="w-4 h-4 text-amber-500"
                    />
                    <span className="text-sm text-neutral-700">{option.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <Label htmlFor="mold-details" className="text-neutral-700">Additional details (Optional)</Label>
              <Textarea
                id="mold-details"
                name="details"
                value={formData.details}
                onChange={handleInputChange}
                placeholder="Any other information that would help us understand your situation..."
                className="mt-1 h-24 resize-none"
              />
            </div>

            <div>
              <Label htmlFor="mold-email" className="text-neutral-700">Email (Optional)</Label>
              <Input
                id="mold-email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="your@email.com"
                className="mt-1"
              />
            </div>

            <div>
              <Label className="text-neutral-700">Preferred contact method</Label>
              <div className="flex gap-4 mt-2">
                {[
                  { value: 'call', label: 'Phone Call' },
                  { value: 'text', label: 'Text Message' },
                  { value: 'email', label: 'Email' }
                ].map(option => (
                  <label key={option.value} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="preferredContact"
                      value={option.value}
                      checked={formData.preferredContact === option.value}
                      onChange={handleInputChange}
                      className="w-4 h-4 text-amber-500"
                    />
                    <span className="text-sm text-neutral-700">{option.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Consent */}
            <div className="bg-neutral-50 rounded-lg p-4">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  name="consent"
                  checked={formData.consent}
                  onChange={handleInputChange}
                  className="w-4 h-4 mt-0.5 text-amber-500 rounded"
                />
                <span className="text-sm text-neutral-600">
                  I agree to be contacted by Gray Contracting regarding my mold project request via phone, text, or email. Standard message and data rates may apply.
                </span>
              </label>
            </div>

            <div className="flex gap-3">
              <Button
                type="button"
                onClick={() => setStep(1)}
                variant="outline"
                className="flex-1"
              >
                Back
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting || !formData.consent}
                className="flex-1 bg-amber-500 hover:bg-amber-600 text-white font-semibold"
              >
                {isSubmitting ? 'Submitting...' : 'Submit Request'}
              </Button>
            </div>
          </div>
        )}

        {/* Direct Contact Options */}
        <div className="mt-6 pt-6 border-t border-neutral-200">
          <p className="text-sm text-neutral-500 text-center mb-3">Or contact us directly:</p>
          <div className="flex gap-3">
            <a href="tel:+12149087644" className="flex-1">
              <Button variant="outline" className="w-full">
                <Phone className="w-4 h-4 mr-2" /> Call 214-908-7644
              </Button>
            </a>
            <TextLink className="flex-1">
              <Button variant="outline" className="w-full">
                <MessageCircle className="w-4 h-4 mr-2" /> Text Photos
              </Button>
            </TextLink>
          </div>
        </div>
      </form>
    </div>
  );
};

export default MoldLeadForm;
